import PropTypes from 'prop-types';
import React, { PureComponent } from 'react';
import get from 'lodash/get';
import orderBy from 'lodash/orderBy';
import scrollIntoView from 'scroll-into-view-if-needed';
import Images from '../utils/Images';
import anonymousAvatar from '../assets/anonymous-avatar.jpg';

const alphabet = 'abcdefghijklmnopqrstuvwxyz';

function nameOf(user) {
  return `${user.lastName || ''} ${user.firstName || ''}`.trim().toLowerCase();
}

function firstLetterOf(user) {
  return nameOf(user)[0] || '';
}

/**
 * Text to display for a field. Select fields store a value, so the label injected as
 * `<field>__label` is preferred when there is one.
 */
function displayValueOf(user, field) {
  const value = get(user, `${field}__label`, get(user, field));
  if (Array.isArray(value)) return value.filter((v) => v).join(', ');
  if (typeof value === 'number') return value.toString();
  return typeof value === 'string' ? value : '';
}

class Speaker extends PureComponent {
  getThumbnail(thumbnail) {
    // TODO: use Images.square
    if (!thumbnail) return anonymousAvatar;
    return Images.square(thumbnail, 400);
  }

  render() {
    const { speaker, fields, isFirst } = this.props;
    const { firstName, lastName, thumbnail } = speaker;
    const details = fields
      .map((field) => ({ field, text: displayValueOf(speaker, field) }))
      .filter(({ text }) => text);
    return (
      <div className="container-speaker">
        {/* place id marker 50px above to fix scroll position issues */}
        {isFirst && (
          <div
            id={`speaker-${firstLetterOf(speaker)}`}
            style={{ position: 'absolute', top: -54 }}
          />
        )}
        <div className="ms-speakers__card">
          <div
            className="ms-speaker__card__image"
            style={{ backgroundImage: `url(${this.getThumbnail(thumbnail)})` }}
          />
          <div className="ms-speaker__card__section">
            <div className="ms-speakers__title">
              {(lastName || '').toUpperCase()}
              <br />
              {firstName}
            </div>
            {details.map(({ field, text }) => (
              <div key={field} className="ms-speakers__details">
                {text}
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }
}

Speaker.defaultProps = {
  fields: [],
  isFirst: false,
};

Speaker.propTypes = {
  speaker: PropTypes.object.isRequired,
  fields: PropTypes.arrayOf(PropTypes.string),
  isFirst: PropTypes.bool,
};

class AlphabetScroller extends React.Component {
  scrollTo = (letter) => {
    // eslint-disable-next-line no-restricted-globals
    const node = document.getElementById(`speaker-${letter}`);
    if (node) {
      scrollIntoView(node, {
        behavior: 'smooth',
        block: 'start',
        inline: 'center',
      });
    }
  };

  render() {
    const { letterIndex, alphabetColor } = this.props;
    return (
      <div className="alphabet">
        {alphabet.split('').map((l) => (
          <button
            type="button"
            key={l}
            className="alphabet-letter"
            disabled={!(l in letterIndex)}
            onClick={() => this.scrollTo(l)}
            style={{ color: alphabetColor }}
          >
            {l}
          </button>
        ))}
      </div>
    );
  }
}

AlphabetScroller.defaultProps = {
  alphabetColor: '',
  letterIndex: undefined,
};

AlphabetScroller.propTypes = {
  alphabetColor: PropTypes.string,
  letterIndex: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
};

export default class Speakers extends PureComponent {
  computeLetterIndex = (speakers) => {
    const letterIndex = {};
    speakers.forEach((speaker, index) => {
      const letter = firstLetterOf(speaker);
      if (!(letter in letterIndex)) {
        letterIndex[letter] = index;
      }
    });
    return letterIndex;
  };

  render() {
    const { speakers, showAlphabet, alphabetColor, maxGridSize, extraField, extraFields } =
      this.props;
    // extraField (single field) kept for the sites configured before extraFields
    const fields = extraFields.length ? extraFields : [extraField].filter((f) => f);
    const sortedSpeakers = orderBy(speakers, nameOf, 'asc');
    const letterIndex = this.computeLetterIndex(sortedSpeakers);
    return (
      <div>
        {showAlphabet && (
          <AlphabetScroller alphabetColor={alphabetColor} letterIndex={letterIndex} />
        )}
        <div className={`speaker-grid max-${maxGridSize}`}>
          {sortedSpeakers.map((speaker, index) => {
            const isFirst = index === letterIndex[firstLetterOf(speaker)];
            return (
              // eslint-disable-next-line react/no-array-index-key
              <Speaker key={`${index}`} speaker={speaker} isFirst={isFirst} fields={fields} />
            );
          })}
          <div style={{ flex: 1 }} />
        </div>
      </div>
    );
  }
}

Speakers.defaultProps = {
  alphabetColor: '',
  extraField: '',
  extraFields: [],
  maxGridSize: '',
  speakers: [],
  showAlphabet: false,
};

Speakers.propTypes = {
  alphabetColor: PropTypes.string,
  extraField: PropTypes.string,
  extraFields: PropTypes.arrayOf(PropTypes.string),
  maxGridSize: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  speakers: PropTypes.arrayOf(PropTypes.object),
  showAlphabet: PropTypes.bool,
};
